import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { ShieldCheck, ShieldAlert, Search, Loader2, QrCode } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicVerify() {
  const { number: paramNumber } = useParams();
  const [number, setNumber] = useState(paramNumber || "");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const verify = async (num) => {
    const n = (num ?? number).trim();
    if (!n) return;
    setLoading(true); setResult(null);
    try {
      const r = await axios.get(`${API}/public/gate-pass/verify/${encodeURIComponent(n)}`);
      setResult(r.data);
    } catch { setResult({ found: false, genuine: false, message: "Verification service unavailable." }); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (paramNumber) verify(paramNumber); }, [paramNumber]); // eslint-disable-line

  const genuine = result?.genuine;

  return (
    <div style={{ minHeight: "100vh", background: "#f1f5f9" }} className="flex items-start justify-center px-4 py-10">
      <div className="w-full max-w-lg">
        <div className="text-center mb-6">
          <div className="h-14 w-14 rounded-2xl flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: "#1e3a5f", color: "#fff" }}>
            <QrCode className="h-7 w-7" />
          </div>
          <h1 className="text-2xl font-bold" style={{ color: "#1e3a5f" }}>{result?.company_name || "BSP MACHINES & TOOLS PVT. LTD."}</h1>
          <p className="text-sm text-slate-500">{result?.project_name || "GPI PROJECT"} · Gate Pass Verification</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5">
          <div className="flex gap-2">
            <input value={number} onChange={(e) => setNumber(e.target.value.toUpperCase())}
              onKeyDown={(e) => { if (e.key === "Enter") verify(); }}
              placeholder="GPI-BSP-YYYYMMDD-0001" data-testid="public-verify-input"
              className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500" />
            <button onClick={() => verify()} disabled={loading} data-testid="public-verify-btn"
              className="rounded-md px-4 py-2 text-sm text-white flex items-center gap-1.5" style={{ backgroundColor: "#1e3a5f" }}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}Verify
            </button>
          </div>
        </div>

        {result && (
          <div data-testid="public-verify-result" className={`mt-5 rounded-xl border-2 p-5 bg-white ${genuine ? "border-emerald-500" : "border-red-500"}`}>
            <div className="flex items-center gap-3 mb-3">
              {genuine ? <ShieldCheck className="h-9 w-9 text-emerald-600" /> : <ShieldAlert className="h-9 w-9 text-red-600" />}
              <div>
                <div className={`text-xl font-bold ${genuine ? "text-emerald-700" : "text-red-700"}`} data-testid="public-verify-verdict">
                  {genuine ? "GENUINE — Issued" : !result.found ? "NOT FOUND" : result.status === "cancelled" ? "CANCELLED" : "NOT ISSUED"}
                </div>
                <div className="text-sm text-slate-500">{result.gate_pass_number || number}</div>
              </div>
            </div>
            {result.found ? (
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><div className="text-[11px] uppercase tracking-wide text-slate-400">Party</div>{result.party_name || "—"}</div>
                <div><div className="text-[11px] uppercase tracking-wide text-slate-400">Vehicle</div>{result.vehicle_number || "—"}</div>
                <div><div className="text-[11px] uppercase tracking-wide text-slate-400">Movement</div>{result.movement_type || "—"}</div>
                <div><div className="text-[11px] uppercase tracking-wide text-slate-400">Purpose</div>{result.purpose || "—"}</div>
                <div><div className="text-[11px] uppercase tracking-wide text-slate-400">Issued</div>{result.issue_date ? `${result.issue_date} ${result.issue_time || ""}` : "—"}</div>
                <div><div className="text-[11px] uppercase tracking-wide text-slate-400">Authorized By</div>{result.authorized_by_name || "—"}</div>
                <div><div className="text-[11px] uppercase tracking-wide text-slate-400">Materials</div>{result.materials_count} item(s)</div>
                {result.status === "cancelled" && <div className="col-span-2 text-red-600"><div className="text-[11px] uppercase tracking-wide text-slate-400">Cancel Reason</div>{result.cancel_reason || "—"}</div>}
              </div>
            ) : (
              <p className="text-sm text-slate-500">{result.message}</p>
            )}
          </div>
        )}
        <p className="text-center text-xs text-slate-400 mt-6">Official gate pass verification · No login required</p>
      </div>
    </div>
  );
}
